/**
 * Server-side error persistence.
 *
 * The existing `utils/errorLogger.ts` only writes localStorage (per-browser), so a
 * failure a user reports is visible nowhere central. This service persists failures to
 * the `app_error_log` table via the `log_client_error` SECURITY DEFINER RPC so we can
 * query intermittent failures (with the PostgREST error code) across all users.
 *
 * Design rules:
 *  - Best-effort: persistence must NEVER throw into the caller's path.
 *  - Throttled: identical (context+message) errors are de-duped within a short window,
 *    and a per-minute cap prevents a failing loop from flooding the table.
 */

import { supabase } from "./supabase";
import {
  logError,
  createErrorLog,
  setErrorLogSink,
  type ErrorLog,
} from "../utils/errorLogger";
import { safeCurrentUrl } from "../utils/safeCurrentUrl";

interface PersistInput {
  context: string;
  operation?: string | null;
  statusCode?: number | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  details?: Record<string, unknown> | null;
  correlationId?: string | null;
}

// --- lightweight client-side throttle (avoid flooding the table) ---
const recent = new Map<string, number>();
const DEDUPE_MS = 10_000;
let windowStart = 0;
let windowCount = 0;
const MAX_PER_MINUTE = 30;

function allow(key: string): boolean {
  const now = Date.now();
  // per-minute cap
  if (now - windowStart > 60_000) {
    windowStart = now;
    windowCount = 0;
  }
  if (windowCount >= MAX_PER_MINUTE) {
    return false;
  }
  // dedupe identical errors
  const last = recent.get(key);
  if (last && now - last < DEDUPE_MS) {
    return false;
  }
  recent.set(key, now);
  windowCount += 1;
  // opportunistic cleanup
  if (recent.size > 200) {
    for (const [k, t] of recent) {
      if (now - t > DEDUPE_MS) {
        recent.delete(k);
      }
    }
  }
  return true;
}

/**
 * Persist a failure to app_error_log (best-effort, throttled, never throws).
 */
export async function persistError(input: PersistInput): Promise<void> {
  try {
    const key = `${input.context}|${input.operation ?? ""}|${input.errorMessage ?? ""}`;
    if (!allow(key)) {
      return;
    }
    await supabase.rpc("log_client_error", {
      p_context: input.context,
      p_operation: input.operation ?? null,
      p_status_code: input.statusCode ?? null,
      p_error_code: input.errorCode ?? null,
      p_error_message: input.errorMessage ?? null,
      p_url: typeof window !== "undefined" ? safeCurrentUrl() : null,
      p_details: input.details ?? null,
      p_correlation_id: input.correlationId ?? null,
    });
  } catch {
    // swallow — observability must not break the app
  }
}

/**
 * Extract the useful fields from a Supabase/PostgREST error (or any Error) and persist
 * it AND mirror it into the existing localStorage logger (so the ErrorLogs page still
 * shows it). Use this at RPC / edge-invoke failure seams.
 */
export function logRpcError(
  context: string,
  operation: string,
  error: unknown,
  details?: Record<string, unknown>,
): void {
  const e = (error ?? {}) as {
    code?: string;
    message?: string;
    details?: string;
    hint?: string;
    status?: number;
  };
  const merged = {
    ...(details ?? {}),
    ...(e.details ? { pg_details: e.details } : {}),
    ...(e.hint ? { pg_hint: e.hint } : {}),
  };
  // localStorage mirror (existing system)
  logError(
    createErrorLog(
      `rpc_error:${context}`,
      error instanceof Error ? error : new Error(e.message ?? "RPC error"),
      {
        operation,
        code: e.code,
        ...merged,
      },
    ),
  );
  // server persistence
  void persistError({
    context: `rpc_error:${context}`,
    operation,
    statusCode: e.status ?? null,
    errorCode: e.code ?? null,
    errorMessage: e.message ?? null,
    details: Object.keys(merged).length ? merged : null,
  });
}

/**
 * Register server persistence as the remote sink for the localStorage error logger, so
 * every logError() — including global window.onerror / unhandledrejection / ErrorBoundary
 * — is also persisted to app_error_log. Call once at app startup.
 */
export function registerServerErrorPersistence(): void {
  setErrorLogSink((log: ErrorLog) => {
    const extra = (log.additionalData ?? {}) as Record<string, unknown>;
    void persistError({
      context: log.type || "client_error",
      operation: typeof extra.operation === "string" ? extra.operation : null,
      errorCode: typeof extra.code === "string" ? extra.code : null,
      errorMessage: log.error,
      details: log.additionalData ?? null,
    });
  });
}
